/**
 * 1.1.2: 知识库服务层（项目级管理）
 * 处理文件上传到 Supabase Storage 并同步到 Python 后端
 * 注意：knowledge_base 实际上就是"项目"，一个项目包含多个文档
 * 1.1.5: 添加创建项目功能
 * 1.2.53: 修复私有bucket文件下载问题，使用signedUrl替代publicUrl
 * 1.4.6: 修复组织切换后项目列表不更新的问题
 * 1.5.0: 添加共享项目权限判断函数
 * 1.6.25: 添加上传队列控制，限制并发上传数量
 * 1.6.27: 添加单URL上传函数，支持逐个URL进度显示
 * 1.6.34: 修复URL上传函数缺少 FastAPI detail 字段检查
 * 1.6.36: 统一 uploadFileToKB 错误消息格式
 */

import { getUserId, supabase } from '../lib/supabase';
import { getCurrentOrganization } from './organizationService'; // 1.4.6: 导入组织服务
import { logger } from '../utils/logger';

const PY_BACKEND_URL = import.meta.env.VITE_PY_BACKEND_URL || 'http://localhost:8000';

// 1.6.25: 上传队列控制 - 限制并发上传数量，避免后端过载
const MAX_CONCURRENT_UPLOADS = 2;  // 最大并发上传数
let currentUploads = 0;
const uploadQueue: Array<() => Promise<void>> = [];

/**
 * 1.6.25: 处理上传队列
 * 当有空闲槽位时，从队列中取出任务执行
 */
function processUploadQueue() {
  while (uploadQueue.length > 0 && currentUploads < MAX_CONCURRENT_UPLOADS) {
    const task = uploadQueue.shift();
    if (task) {
      currentUploads++;
      task().finally(() => {
        currentUploads--;
        processUploadQueue();
      });
    }
  }
}

/**
 * 1.1.5: 创建新项目（知识库）
 * 1.3.79: 添加 organization_id 支持，满足 RLS 策略要求
 * 1.4.6: 使用前端选择的组织，而不是数据库默认逻辑
 */
export async function createKnowledgeBase(name: string, description: string, userId: string) {
  try {
    // 1.4.6: 使用前端选择的当前组织
    const { organization, error: orgError } = await getCurrentOrganization();

    if (orgError || !organization) {
      logger.error('Error getting organization:', orgError);
      throw new Error('用户未关联任何组织，无法创建项目');
    }

    const organizationId = organization.id;

    // 生成唯一的向量集合名称
    const vectorCollection = `kb_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    
    // 生成分享令牌
    const shareToken = crypto.randomUUID();

    const { data, error } = await supabase
      .from('knowledge_bases')
      .insert({
        name,
        description,
        user_id: userId,
        organization_id: organizationId, // 1.3.79: 添加组织ID
        vector_collection: vectorCollection,
        share_token: shareToken,
        dify_dataset_id: null, // 1.1.5: 不再使用 Dify
      })
      .select()
      .single();

    if (error) throw error;

    logger.log('Knowledge base created:', data);
    return data;
  } catch (error) {
    logger.error('Error creating knowledge base:', error);
    throw error;
  }
}

/**
 * 1.1.5: 获取所有项目列表
 * 1.3.90: 改用 RPC 函数获取用户有权访问的知识库（支持多租户）
 * 1.4.6: 修复组织切换后项目列表不更新的问题，使用前端选择的组织
 */
export async function listKnowledgeBases(userId: string) {
  try {
    // 1.4.6: 使用前端选择的当前组织，而不是数据库默认逻辑
    const { organization, error: orgError } = await getCurrentOrganization();

    if (orgError) {
      logger.error('Error getting organization:', orgError);
      // 如果获取组织失败，回退到旧逻辑
      const { data, error } = await supabase
        .from('knowledge_bases')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    }

    if (!organization) {
      logger.log('User has no organization');
      return [];
    }

    const organizationId = organization.id;
    logger.log('Listing knowledge bases for organization:', organizationId, organization.name);

    // 1.3.90: 查询该组织下用户创建的或共享的知识库
    const { data, error } = await supabase
      .from('knowledge_bases')
      .select('*')
      .eq('organization_id', organizationId)
      .or(`user_id.eq.${userId},is_shared.eq.true`)
      .order('created_at', { ascending: false });

    if (error) throw error;
    logger.log('Found knowledge bases:', data?.length || 0);
    return data || [];
  } catch (error) {
    logger.error('Error listing knowledge bases:', error);
    throw error;
  }
}

/**
 * 1.6.25: 修改为支持传入 docId，用于后端精确匹配文档更新字数
 */
export async function uploadFileToKB(kbId: string, file: File, docId?: string) {
  // 1.6.46: 上传处理使用 Authorization 以强制租户隔离
  const { isAuthenticated } = await getUserId();
  if (!isAuthenticated) {
    throw new Error('请先登录后再上传文件');
  }
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) {
    throw new Error('未获取到登录凭证');
  }

  // 1. 获取项目的 vector_collection
  const { data: kb, error: kbError } = await supabase
    .from('knowledge_bases')
    .select('vector_collection')
    .eq('id', kbId)
    .single();

  if (kbError || !kb) throw new Error('Knowledge base not found');

  const collectionName = kb.vector_collection;

  // 2. 上传到 Supabase Storage
  const fileExt = file.name.split('.').pop();
  const fileName = `${Math.random()}.${fileExt}`;
  const filePath = `${kbId}/${fileName}`;

  const { data, error } = await supabase.storage
    .from('knowledge-base-files')
    .upload(filePath, file);

  if (error) throw error;

  // 3. 1.2.53: 获取文件的签名 URL（bucket 是私有的，不能使用 getPublicUrl）
  // 签名 URL 有效期 1 小时（3600 秒），足够后端下载和处理
  const { data: signedUrlData, error: signedUrlError } = await supabase.storage
    .from('knowledge-base-files')
    .createSignedUrl(filePath, 3600);

  if (signedUrlError || !signedUrlData?.signedUrl) {
    throw new Error(`无法生成文件访问URL: ${signedUrlError?.message || '未知错误'}`);
  }

  const fileUrl = signedUrlData.signedUrl;

  // 4. 通知 Python 后端处理，使用项目的统一 vector_collection
  // 1.6.25: 传入 doc_id 用于精确匹配文档更新字数
  const response = await fetch(`${PY_BACKEND_URL}/api/process-file`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      file_path: fileUrl,
      collection_name: collectionName, // 1.1.2: 使用项目的向量集合
      doc_id: docId, // 1.6.25: 传入文档ID用于精确匹配
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    // 1.6.36: 统一错误消息格式，与 URL 上传函数保持一致
    throw new Error(errorData.message || errorData.detail || 'Failed to process file in backend');
  }

  return await response.json();
}

export async function getKnowledgeBase(kbId: string) {
  const { data, error } = await supabase
    .from('knowledge_bases')
    .select('*')
    .eq('id', kbId)
    .single();

  if (error) throw error;
  return data;
}

/**
 * 1.1.11: 上传URL到知识库（批量）
 * @deprecated 1.6.27: 推荐使用 uploadSingleUrlToKB 逐个处理，支持进度显示
 */
export async function uploadUrlsToKB(kbId: string, urls: string[]) {
  const { isAuthenticated } = await getUserId();
  if (!isAuthenticated) {
    throw new Error('请先登录后再上传URL');
  }
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) {
    throw new Error('未获取到登录凭证');
  }

  // 1. 获取项目的 vector_collection
  const { data: kb, error: kbError } = await supabase
    .from('knowledge_bases')
    .select('vector_collection')
    .eq('id', kbId)
    .single();

  if (kbError || !kb) throw new Error('Knowledge base not found');

  const collectionName = kb.vector_collection;

  // 2. 调用 Python 后端处理URL
  const response = await fetch(`${PY_BACKEND_URL}/api/process-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      urls: urls,
      collection_name: collectionName, // 1.1.11: 使用项目的向量集合
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    // 1.6.34: 同时检查 message 和 detail，兼容 FastAPI HTTPException
    throw new Error(errorData.message || errorData.detail || 'Failed to process URLs in backend');
  }

  return await response.json();
}

/**
 * 1.6.27: 上传单个URL到知识库
 * 用于逐个处理URL并显示进度
 * @param kbId 知识库ID
 * @param url 要处理的URL
 * @param docId 文档ID（用于后端精确匹配更新字数）
 * @returns Promise 处理结果
 */
export async function uploadSingleUrlToKB(kbId: string, url: string, docId?: string) {
  const { isAuthenticated } = await getUserId();
  if (!isAuthenticated) {
    throw new Error('请先登录后再上传URL');
  }
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) {
    throw new Error('未获取到登录凭证');
  }

  // 1. 获取项目的 vector_collection
  const { data: kb, error: kbError } = await supabase
    .from('knowledge_bases')
    .select('vector_collection')
    .eq('id', kbId)
    .single();

  if (kbError || !kb) throw new Error('Knowledge base not found');

  const collectionName = kb.vector_collection;

  // 2. 调用 Python 后端处理单个URL
  const response = await fetch(`${PY_BACKEND_URL}/api/process-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      urls: [url], // 1.6.27: 单URL作为数组传递，复用现有后端接口
      collection_name: collectionName,
      doc_id: docId, // 1.6.27: 传入文档ID用于精确匹配
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    // 1.6.34: 同时检查 message 和 detail，兼容 FastAPI HTTPException
    throw new Error(errorData.message || errorData.detail || 'Failed to process URL in backend');
  }

  return await response.json();
}

export async function updateShareToken(kbId: string) {
  const { data, error } = await supabase
    .from('knowledge_bases')
    .update({ share_token: crypto.randomUUID() })
    .eq('id', kbId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * 1.5.0: 判断当前用户是否是知识库的创建者（所有者）
 * 用于共享项目中的权限控制
 * @param kb 知识库对象
 * @param userId 当前用户ID
 * @returns boolean - 是否是知识库所有者
 */
export function isKnowledgeBaseOwner(kb: { user_id: string } | null, userId: string | undefined): boolean {
  if (!kb || !userId) return false;
  return kb.user_id === userId;
}

/**
 * 1.5.0: 判断当前用户是否可以管理知识库（完全控制权限）
 * 所有者或组织 Owner 可以管理知识库
 * @param kb 知识库对象
 * @param userId 当前用户ID
 * @param isOrgOwner 是否是组织 Owner
 * @returns boolean - 是否可以管理知识库
 */
export function canManageKnowledgeBase(
  kb: { user_id: string } | null,
  userId: string | undefined,
  isOrgOwner: boolean = false
): boolean {
  if (!kb || !userId) return false;
  return kb.user_id === userId || isOrgOwner;
}

/**
 * 1.6.25: 带队列控制的文件上传
 * 限制并发上传数量，避免后端过载
 * 1.6.30: 添加 docId 参数支持，用于后端精确匹配文档更新字数
 * @param kbId 知识库ID
 * @param file 要上传的文件
 * @param docId 文档ID（可选，用于后端精确匹配）
 * @returns Promise 上传结果
 */
export async function uploadFileToKBWithQueue(kbId: string, file: File, docId?: string): Promise<unknown> {
  return new Promise((resolve, reject) => {
    uploadQueue.push(async () => {
      try {
        logger.log(`📤 开始上传文件: ${file.name} (队列位置: ${uploadQueue.length}, 当前并发: ${currentUploads})`);
        // 1.6.30: 传递 docId 参数用于精确匹配
        const result = await uploadFileToKB(kbId, file, docId);
        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
    processUploadQueue();
  });
}

/**
 * 1.6.25: 获取当前上传队列状态
 * 用于调试和监控
 */
export function getUploadQueueStatus() {
  return {
    queueLength: uploadQueue.length,
    currentUploads,
    maxConcurrent: MAX_CONCURRENT_UPLOADS
  };
}
